import React, { useEffect, useState } from "react";
import axios from "axios";
import PayPalSubscriptionButton from "./PayPalSubscriptionButton";

const API = axios.create({
  baseURL: "http://localhost:5000/api",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

function loadRazorpayScript() {
  return new Promise((resolve) => {
    const existing = document.getElementById("razorpay-sdk");
    if (existing) return resolve(true);

    const script = document.createElement("script");
    script.id = "razorpay-sdk";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function SubscriptionPlans({
  onSubscriptionActivated,
  onPaymentSuccess,
  theme = "dark",
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState({
    companyName: "",
    contactName: "",
    email: "",
    mobileNumber: "",
    requirements: "",
  });

  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const isDark = theme === "dark";

  const colors = {
    pageText: isDark ? "#f8fafc" : "#111827",
    subText: isDark ? "#cbd5e1" : "#6b7280",
    cardBg: isDark ? "#111827" : "#ffffff",
    border: isDark ? "#334155" : "#d1d5db",
    inputBg: isDark ? "#0f172a" : "#ffffff",
    inputText: isDark ? "#f8fafc" : "#111827",
    badgeBg: isDark ? "#1e293b" : "#eff6ff",
    badgeText: isDark ? "#93c5fd" : "#1d4ed8",
    softBg: isDark ? "#0b1220" : "#f8fafc",
    dangerBg: isDark ? "#3f1d1d" : "#fef2f2",
    dangerText: isDark ? "#fecaca" : "#991b1b",
    dangerBorder: isDark ? "#7f1d1d" : "#fecaca",
    shadow: isDark
      ? "0 12px 30px rgba(0,0,0,0.35)"
      : "0 12px 30px rgba(15,23,42,0.08)",
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  async function fetchPlans() {
    try {
      const res = await API.get("/subscription/plans");
      setPlans(res?.data?.plans || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch subscription plans");
    }
  }

  async function handleRazorpayBuy() {
    try {
      console.log("Razorpay clicked");
      setLoading(true);

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded || !window.Razorpay) {
        alert("Razorpay checkout failed to load. Please check internet connection.");
        return;
      }

      const res = await API.post("/subscription/razorpay/create-order", {
        plan: "starter",
      });

      const order = res.data;

      if (!order || !order.orderId) {
        alert("Order creation failed. Please check backend.");
        return;
      }

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: "Bivora Smart Analytics",
        description: "Starter Plan",
        order_id: order.orderId,

        handler: async function (response) {
          try {
            const verifyRes = await API.post("/subscription/razorpay/verify", {
              plan: "starter",
              razorpay_order_id: order.orderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            alert(verifyRes?.data?.message || "Payment successful");

            if (onSubscriptionActivated) onSubscriptionActivated();

            if (onPaymentSuccess) {
              onPaymentSuccess({
                provider: "Razorpay",
                planName: "Starter",
                paymentId: response.razorpay_payment_id,
              });
            }
          } catch (err) {
            console.error("Razorpay verification error:", err);
            alert(err?.response?.data?.message || "Payment verification failed");
          }
        },

        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },

        theme: {
          color: "#2563eb",
        },

        modal: {
          ondismiss: function () {
            console.log("Razorpay popup closed");
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.error("Razorpay payment failed:", response?.error);
        alert(response?.error?.description || "Payment failed");
      });

      rzp.open();
    } catch (error) {
      console.error("Razorpay error:", error);
      alert(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to create Razorpay order"
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleContactSubmit(e) {
    e.preventDefault();

    try {
      const res = await API.post("/enterprise-leads", contactForm);

      alert(res?.data?.message || "Enterprise request submitted successfully");

      setContactForm({
        companyName: "",
        contactName: "",
        email: "",
        mobileNumber: "",
        requirements: "",
      });
    } catch (error) {
      console.error(error);
      alert(
        error?.response?.data?.message ||
          "Failed to submit enterprise request"
      );
    }
  }

  const starterPlan = plans.find((p) => p.id === "starter");
  const personalizedPlan = plans.find((p) => p.id === "personalized");

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ color: colors.pageText, marginBottom: 8 }}>
          Choose Your Plan
        </h2>
        <p style={{ color: colors.subText, margin: 0 }}>
          Demo dashboards stay visible for everyone. Starter includes a 1-file
          free trial. Premium unlocks upload, reports, PDF export, and print.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 22,
        }}
      >
        {starterPlan && (
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 22,
              padding: 24,
              boxShadow: colors.shadow,
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: 999,
                background: colors.badgeBg,
                color: colors.badgeText,
                fontWeight: 700,
                fontSize: 12,
                marginBottom: 14,
              }}
            >
              STARTER
            </div>

            <h3
              style={{
                color: colors.pageText,
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              {starterPlan.name}
            </h3>

            <div
              style={{ color: colors.pageText, fontSize: 34, fontWeight: 800 }}
            >
              $10{" "}
              <span
                style={{ fontSize: 16, fontWeight: 500, color: colors.subText }}
              >
                / month
              </span>
            </div>

            <p style={{ color: colors.subText, marginTop: 10 }}>
              Approx ₹899 / month
            </p>

            <div
              style={{
                marginTop: 18,
                background: colors.softBg,
                border: `1px solid ${colors.border}`,
                borderRadius: 16,
                padding: 16,
              }}
            >
              <ul
                style={{ paddingLeft: 18, margin: 0, color: colors.pageText }}
              >
                {(starterPlan.features || []).map((item, index) => (
                  <li key={index} style={{ marginBottom: 10 }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginTop: 22 }}>
              <h4 style={{ color: colors.pageText, marginBottom: 10 }}>
                Pay with PayPal
              </h4>

              {paypalClientId &&
              starterPlan.paypalPlanId &&
              starterPlan.paypalPlanId !== "P-XXXXXXXXXXXX" ? (
                <PayPalSubscriptionButton
                  clientId={paypalClientId}
                  planId={starterPlan.paypalPlanId}
                  onActivated={() => {
                    if (onSubscriptionActivated) onSubscriptionActivated();

                    if (onPaymentSuccess) {
                      onPaymentSuccess({
                        provider: "PayPal",
                        planName: "Starter",
                      });
                    }
                  }}
                />
              ) : (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: colors.dangerBg,
                    color: colors.dangerText,
                    border: `1px solid ${colors.dangerBorder}`,
                  }}
                >
                  PayPal is not ready yet. A real PayPal Plan ID is still
                  missing.
                </div>
              )}
            </div>

            <div style={{ marginTop: 20 }}>
              <h4 style={{ color: colors.pageText, marginBottom: 10 }}>
                Or pay with Razorpay
              </h4>

              <button
                onClick={handleRazorpayBuy}
                disabled={loading}
                style={{
                  width: "100%",
                  padding: 14,
                  border: "none",
                  borderRadius: 12,
                  background: loading ? "#64748b" : "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Please wait..." : "Pay with Razorpay"}
              </button>
            </div>
          </div>
        )}

        {personalizedPlan && (
          <div
            style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 22,
              padding: 24,
              boxShadow: colors.shadow,
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "6px 12px",
                borderRadius: 999,
                background: "#7c3aed",
                color: "#ffffff",
                fontWeight: 800,
                fontSize: 12,
                marginBottom: 14,
              }}
            >
              ENTERPRISE
            </div>

            <h3
              style={{
                color: colors.pageText,
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              {personalizedPlan.name || "Personalized"}
            </h3>

            <div
              style={{ color: colors.pageText, fontSize: 34, fontWeight: 900 }}
            >
              Custom
            </div>

            <p style={{ color: colors.subText, marginTop: 10, lineHeight: 1.6 }}>
              For global teams, custom dashboards, onboarding, white-label
              reports, training, and dedicated support.
            </p>

            <form onSubmit={handleContactSubmit} style={{ marginTop: 20 }}>
              <input
                style={inputStyle(colors)}
                type="text"
                placeholder="Company Name"
                value={contactForm.companyName}
                onChange={(e) =>
                  setContactForm((p) => ({
                    ...p,
                    companyName: e.target.value,
                  }))
                }
                required
              />

              <input
                style={inputStyle(colors)}
                type="text"
                placeholder="Contact Name"
                value={contactForm.contactName}
                onChange={(e) =>
                  setContactForm((p) => ({
                    ...p,
                    contactName: e.target.value,
                  }))
                }
                required
              />

              <input
                style={inputStyle(colors)}
                type="email"
                placeholder="Work Email"
                value={contactForm.email}
                onChange={(e) =>
                  setContactForm((p) => ({ ...p, email: e.target.value }))
                }
                required
              />

              <input
                style={inputStyle(colors)}
                type="text"
                placeholder="Mobile Number"
                value={contactForm.mobileNumber}
                onChange={(e) =>
                  setContactForm((p) => ({
                    ...p,
                    mobileNumber: e.target.value,
                  }))
                }
              />

              <textarea
                style={{ ...inputStyle(colors), minHeight: 110 }}
                placeholder="Tell us what you need"
                value={contactForm.requirements}
                onChange={(e) =>
                  setContactForm((p) => ({
                    ...p,
                    requirements: e.target.value,
                  }))
                }
                required
              />

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: 15,
                  border: "none",
                  borderRadius: 14,
                  background: "linear-gradient(135deg, #7c3aed, #2563eb)",
                  color: "#fff",
                  fontWeight: 900,
                  cursor: "pointer",
                  boxShadow: "0 16px 36px rgba(37,99,235,0.28)",
                }}
              >
                Contact Us for Enterprise Plan
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function inputStyle(colors) {
  return {
    width: "100%",
    marginBottom: 10,
    padding: 12,
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.inputText,
  };
}